import { useCallback, useState } from 'react';

import { useMount } from '@simplywallst/ui-core';
import { sentryCaptureMessage } from '@/components/Sentry';

import { useSnackbar } from '@/features/snackbar';
import { usePersistentStorage } from '@/hooks/useCoupon';

import { useReduxStateForPortfolioLinkingSuccess } from '../../../pages/PortfolioLinkingSuccess/hooks/useReduxStateForPortfolioLinkingSuccess';
import { PlaidPortfolioLinkingPortal } from './PlaidPortfolioLinkingPortal';
import { useInitializePortfolioConnection } from '../hooks/useInitializePortfolioConnection';
import { PLAID_LINK_TOKEN_KEY } from './constants';

import type {
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';
import type { PlaidEvent } from './types';
import { getConnectionData } from './utils/getConnectionData';

type Props = {
  onSuccess: () => void;
  onError: () => void;
};

export const PlaidPortfolioLinkingSuccess = ({
  onSuccess: handleSuccess,
  onError: handleError,
}: Props) => {
  const { plaidOAuthStateId } = useReduxStateForPortfolioLinkingSuccess();
  const { get } = usePersistentStorage();
  const { addSnackbar } = useSnackbar();

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [openPlaidLinkPortal, setOpenPlaidLinkPortal] = useState(false);

  const { mutate } = useInitializePortfolioConnection();

  useMount(() => {
    if (RUNTIME_ENV === 'browser' && typeof plaidOAuthStateId === 'undefined') {
      sentryCaptureMessage({
        team: 'wombats',
        message: 'Plaid OAuth State ID is not available in redirect page url',
        extra: { plaidOAuthStateId },
      });
      handleError();
      return;
    }

    get(PLAID_LINK_TOKEN_KEY).then((persistedToken) => {
      if (persistedToken === null) {
        sentryCaptureMessage({
          team: 'wombats',
          message: 'Plaid link token retrieved from persistent storage is null',
        });
        handleError();
        return;
      }

      setLinkToken(persistedToken);
      setOpenPlaidLinkPortal(true);
    });
  });

  const onSuccess = useCallback(
    (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      mutate(
        {
          connection: getConnectionData(publicToken, metadata),
          provider: 'plaid',
          platform: RUNTIME_ENV === 'native' ? 'native' : 'browser',
        },
        {
          onError: handleError,
          onSuccess: handleSuccess,
        }
      );
    },
    [handleError, handleSuccess, mutate]
  );

  const onError = useCallback(
    (
      error: PlaidLinkError,
      metadata: PlaidLinkOnExitMetadata,
      events: PlaidEvent[]
    ) => {
      sentryCaptureMessage({
        team: 'wombats',
        message: 'Plaid portfolio linking error in redirect flow',
        extra: {
          error,
          metadata,
          events,
        },
      });
      addSnackbar({
        type: 'negative',
        message:
          error.display_message ||
          'Something went wrong, please start the portfolio linking process again',
        lifeTime: 10000,
      });
      handleError();
    },
    [addSnackbar, handleError]
  );

  const onClose = useCallback(() => {
    addSnackbar({
      type: 'warning',
      message:
        'Please refresh the page and complete the final step of the portfolio linking process',
      lifeTime: 10000,
    });
  }, [addSnackbar]);

  if (!openPlaidLinkPortal || linkToken === null) return null;

  return (
    <PlaidPortfolioLinkingPortal
      linkToken={linkToken}
      receivedRedirectUri={
        RUNTIME_ENV === 'browser' ? window.location.href : undefined
      }
      onSuccess={onSuccess}
      onError={onError}
      onClose={onClose}
    />
  );
};
